import type { ManagerAvatarProvider, ManagerAvatarProviderModule } from './AvatarProvider.js';
import type { ManagerAvatarInput } from './types.js';

type AvatarProviderLoader = () => Promise<ManagerAvatarProviderModule>;

const avatarProviderLoaders = new Map<string, AvatarProviderLoader>([
    ['avatarmaker', () => import('./providers/avatarmaker-provider.js')]
]);

const defaultProviderKey = 'avatarmaker';

let activeProvider: ManagerAvatarProvider = createFallbackProvider();
let activeProviderKey = 'fallback';
let providerLoadPromise: Promise<void> | null = null;

const providerListeners = new Set<(provider: ManagerAvatarProvider) => void>();

export function getManagerAvatarSvg(player: ManagerAvatarInput): string {
    return activeProvider.getAvatarSvg(player);
}

export function getManagerAvatarProviderInfo(): Pick<ManagerAvatarProvider, 'key' | 'version' | 'profile'> {
    return {
        key: activeProvider.key,
        version: activeProvider.version,
        profile: activeProvider.profile
    };
}

export function onManagerAvatarProviderChanged(listener: (provider: ManagerAvatarProvider) => void): () => void {
    providerListeners.add(listener);
    return () => {
        providerListeners.delete(listener);
    };
}

export function registerManagerAvatarProviderLoader(key: string, loader: AvatarProviderLoader): void {
    const safeKey = normalizeProviderKey(key);
    avatarProviderLoaders.set(safeKey, loader);
}

export function warmManagerAvatarProvider(): Promise<void> {
    if (providerLoadPromise) {
        return providerLoadPromise;
    }
    const providerKey = resolveConfiguredProviderKey();
    const loader = avatarProviderLoaders.get(providerKey) ?? avatarProviderLoaders.get(defaultProviderKey);
    if (!loader) {
        return Promise.resolve();
    }
    providerLoadPromise = loader()
        .then((module) => {
            const nextProvider = module.createManagerAvatarProvider();
            activeProvider = nextProvider;
            activeProviderKey = providerKey;
            notifyProviderListeners(nextProvider);
        })
        .catch((error: unknown) => {
            console.warn('Avatar provider load failed. Falling back to lightweight avatars.', error);
        });
    return providerLoadPromise;
}

function notifyProviderListeners(provider: ManagerAvatarProvider): void {
    for (const listener of providerListeners) {
        listener(provider);
    }
}

function resolveConfiguredProviderKey(): string {
    const configured = (globalThis as { __TSM_AVATAR_PROVIDER__?: string }).__TSM_AVATAR_PROVIDER__;
    const safeKey = normalizeProviderKey(configured ?? defaultProviderKey);
    if (!avatarProviderLoaders.has(safeKey) && avatarProviderLoaders.has(defaultProviderKey)) {
        return defaultProviderKey;
    }
    return safeKey;
}

function normalizeProviderKey(value: string): string {
    const collapsed = String(value || '').trim().toLowerCase();
    return collapsed || defaultProviderKey;
}

function createFallbackProvider(): ManagerAvatarProvider {
    return {
        key: 'fallback',
        version: 'fallback-v1',
        profile: 'semantic',
        getAvatarSvg: buildFallbackAvatarSvg,
        clearCache: () => {
            // No-op: fallback provider is pure and cache-free.
        }
    };
}

function buildFallbackAvatarSvg(player: ManagerAvatarInput): string {
    const initials = getInitials(player.name);
    const bg = getRoleColor(player.role);
    const fg = getContrastColor(bg);
    const hair = numberToHexColor(player.appearance?.hairColor, '3b2a1c');
    const skin = numberToHexColor(player.appearance?.skinTone, 'b88c6a');

    return `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${escapeAttr(player.name)} avatar"><rect width="80" height="80" rx="12" fill="${bg}"/><circle cx="40" cy="36" r="18" fill="${skin}"/><path d="M22 30c3-11 12-16 18-16s15 5 18 16v8H22z" fill="${hair}"/><rect x="16" y="56" width="48" height="18" rx="9" fill="rgba(0,0,0,0.28)"/><text x="40" y="69" text-anchor="middle" font-size="14" font-family="system-ui, sans-serif" font-weight="700" fill="${fg}">${escapeText(initials)}</text></svg>`;
}

function getInitials(name: string): string {
    const pieces = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (pieces.length === 0) {
        return 'PL';
    }
    if (pieces.length === 1) {
        return pieces[0].slice(0, 2).toUpperCase();
    }
    return `${pieces[0][0]}${pieces[1][0]}`.toUpperCase();
}

function getRoleColor(role: string): string {
    const upper = String(role || '').toUpperCase();
    if (upper === 'GK') {
        return '#2f7d56';
    }
    if (['LB', 'CB', 'RB', 'LWB', 'RWB'].includes(upper)) {
        return '#365693';
    }
    if (['LW', 'RW', 'ST', 'CF'].includes(upper)) {
        return '#933d35';
    }
    if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(upper)) {
        return '#84682e';
    }
    return '#415669';
}

function getContrastColor(hex: string): string {
    const safe = hex.replace('#', '');
    const r = Number.parseInt(safe.slice(0, 2), 16);
    const g = Number.parseInt(safe.slice(2, 4), 16);
    const b = Number.parseInt(safe.slice(4, 6), 16);
    const luma = (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
    return luma > 144 ? '#10202f' : '#f5f8fc';
}

function numberToHexColor(value: unknown, fallback: string): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return `#${fallback}`;
    }
    const clamped = Math.max(0, Math.min(0xffffff, Math.round(parsed)));
    return `#${clamped.toString(16).padStart(6, '0')}`;
}

function escapeText(value: string): string {
    return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
}

function escapeAttr(value: string): string {
    return escapeText(value)
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}
